// GenerativeAnswerComponent.jsx
import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { GeneratedAnswer, GeneratedAnswerState, GeneratedAnswerCitation } from '@coveo/headless';
import classNames from 'classnames';
var showdown  = require('showdown'),
markdownConverter = new showdown.Converter({headerLevelStart: 4});

interface GeneratedAnswerComponentProps {
	generatedAnswer: GeneratedAnswer;
	heading?: String;
}

export function Citation( {
	clickUri,
	text,
	title,
} ) {
	const [ active, setActive ] = useState( false );

	return (
		<li
			className={ `coveo-advanced-query__citation coveo-advanced-query__citation--${ active ? 'active' : 'inactive' }` }
			onPointerOver={ ()  => setActive( true ) }
			onPointerOut={ () => setActive( false ) }
		>
			<a
				className="coveo-advanced-query__citation-link"
				href={ clickUri }
				target="_blank"
				rel="noopener noreferrer"
				title={ title }
			>
				<span className="coveo-advanced-query__citation-title">
					<span>
						{ title }
					</span>
				</span>
				<div className="coveo-advanced-query__citation-desc">
					{ title }
					<br />
					<br />
					{ text }
				</div>
			</a>
		</li>
	);
}

export const GenerativeAnswerComponent: React.FC< GeneratedAnswerComponentProps > = ( {
	generatedAnswer,
	heading = 'AI Search Summary',
} ) => {
	const [
		generatedAnswerState,
		setGeneratedAnswerState,
	] = useState< GeneratedAnswerState >( generatedAnswer.state );
	const ref = useRef();

	const [ truncated, setTruncated ] = useState<Boolean>( true );
	const [ expanded, setExpanded ] = useState<Boolean>( false );

	const onGeneratedAnswerStateUpdate = useCallback( () => {
		setGeneratedAnswerState( generatedAnswer.state );
	}, [ generatedAnswer ] );

	const isGenerating = !! generatedAnswerState?.isStreaming;
	const answer = generatedAnswerState?.answer;
	const citations = generatedAnswerState?.citations;
	const hasCitations = !! citations?.length;

	// Subscribe to the generative answer state on component mount.
	useEffect( () => {
		const unsubscribe = generatedAnswer.subscribe( () => {
			setGeneratedAnswerState( generatedAnswer.state );
		} );

		generatedAnswer.subscribe( onGeneratedAnswerStateUpdate ); // Subscribe to state changes

		return () => {
			unsubscribe();
		};
	}, [
		generatedAnswer,
		onGeneratedAnswerStateUpdate,
	] );

	useLayoutEffect( () => {
		const measure = () => ref?.current && ref?.current?.offsetHeight < ref?.current?.scrollHeight;
		setTruncated( measure() );

		window.addEventListener( 'resize', measure );

		return () => {
			if ( ref?.current ) {
				window.removeEventListener( 'resize', measure );
			}
		}
	}, [ ref?.current ] );

	const [
		showCitationText,
		setShowCitationText,
	] = useState( false );

	if ( ! answer ) {
		return null;
	}

	// console.log( { truncated } );

	return (
		<div className="coveo-advanced-query__summary has-background has-purple-background-color alignfull">
			<div className="coveo-advanced-query__summary-layout">
				<div className="coveo-advanced-query__summary-content">
					<h2 className="has-kicker-variant has-white-color">{ heading }</h2>
					{ isGenerating ? '... [generating]' : '' }
					<div
						ref={ ref }
						className={ classNames(
							'coveo-advanced-query__summary-answer',
							{
								'coveo-advanced-query__summary-answer--truncated': truncated,
								'coveo-advanced-query__summary-answer--expanded': expanded,
							}
						) }
						dangerouslySetInnerHTML={ {
							__html: markdownConverter.makeHtml( answer ),
						} }
					>
					</div>
					{ ( truncated && ! expanded ) && (
						<button
							onClick={ () => setExpanded( true ) }
							className="coveo-advanced-query__summary-expand"
							title="Expand Answer"
						>
							<svg
								className="svg coveo-advanced-query__facets-toggle-icon"
								role="img"
								focusable="false"
								height="24"
								width="24"
							>
								<use xlinkHref="#svg-assets-svg-icon-angle-down"></use>
							</svg>
						</button>
					) }
				</div>
				<div className="coveo-advanced-query__summary-aside">
					{ hasCitations ? (
						<>
							<p className="has-kicker-variant has-white-color">Citations</p>
							<ul className="coveo-advanced-query__citations">
								{ citations.map( ( citation, index ) => (
									<Citation
										key={ index }
										{ ...citation }
									/>
								) ) }
							</ul>
						</>
					) : (
						answer && <p>No citations available.</p>
					) }
				</div>
			</div>
		</div>
	);
};
